import type { Bridge } from '@casa/bridge';
import { describeServer, isServerPage, listServerDashboards, readServerDashboard, serverLink, writeServerDashboard, type ServerStore } from './casa_server.ts';
import { fetchFromUrl, parseOrThrow, readSource, SERVER_NAMES } from './dashboard.ts';
import { findDeviceDashboard, listDeviceDashboards, readDeviceDashboard, writeDeviceDashboard } from './device_store.ts';

// The stores that keep dashboards (docs/specs/settings.md §4.2): this device (the default), the
// Tilez Hub the device is paired with, Tilez Cloud beside it (ST-10), and a web address, which is read-only. The user picks the
// current store in the settings and nowhere else (ST-2); its dashboards are listed there, and new
// dashboards go into it. A browser served by the Tilez Hub has that server as its only store (ST-6).

/** Which store is current, and the web address's settings (kept while another store is current). */
export const STORES_PATH = 'stores.json';

export type StoreKind = 'device' | ServerStore | 'url';

/** The stores a dashboard can be saved in. */
export type WritableStore = 'device' | ServerStore;

export const isServerStore = (kind: StoreKind) : kind is ServerStore => kind === 'casa' || kind === 'cloud';

export interface WebAddress {
    url: string;
    authorization?: string;
}

export interface StoreSettings {
    current: StoreKind;
    url?: WebAddress | undefined;
}

export interface StoreInfo {
    kind: StoreKind;
    /** "This device", "Tilez Hub", "Tilez Cloud", "Web address". */
    title: string;
    /** Where it is: the server's address, the web address. */
    detail?: string | undefined;
    /** Read-only stores (a web address) take no new dashboards and no saves (ST-8). */
    isWritable: boolean;
}

export interface StoreEntry {
    name: string;
    /** When it was last saved (ISO 8601), where the store says. */
    updated?: string | undefined;
}

function isWebAddress(value: unknown) : value is WebAddress {
    const candidate = value as Partial<WebAddress> | undefined;

    return typeof candidate?.url === 'string' && (candidate.authorization === undefined || typeof candidate.authorization === 'string');
}

/**
 * The store settings. Without any (a device set up before the stores), they follow where the shown
 * dashboard comes from, so that the device carries on as it was.
 */
export async function readStoreSettings(bridge: Bridge) : Promise<StoreSettings> {
    if (isServerPage()) {
        return { current: 'casa' };
    }

    const stored = await bridge.storage.read(STORES_PATH);
    if (stored) {
        try {
            const parsed = JSON.parse(new TextDecoder().decode(stored)) as Partial<StoreSettings>;
            if (parsed.current === 'device' || parsed.current === 'casa' || parsed.current === 'cloud' || parsed.current === 'url') {
                return { current: parsed.current, url: isWebAddress(parsed.url) ? parsed.url : undefined };
            }
        } catch {
            // Not readable: as if there were none.
        }
    }

    const source = await readSource(bridge);
    if (source?.kind === 'url') {
        return { current: 'url', url: { url: source.url, ...(source.authorization ? { authorization: source.authorization } : {}) } };
    }

    return { current: source?.kind === 'casa' || source?.kind === 'cloud' ? source.kind : 'device' };
}

export async function writeStoreSettings(bridge: Bridge, settings: StoreSettings) : Promise<void> {
    await bridge.storage.write(STORES_PATH, new TextEncoder().encode(`${JSON.stringify(settings, null, 2)}\n`));
}

/** The stores this device can use now: this device (not in a browser), the paired server, Tilez Cloud, the web address. */
export async function availableStores(bridge: Bridge) : Promise<StoreInfo[]> {
    const stores: StoreInfo[] = [];
    if (!isServerPage()) {
        stores.push({ kind: 'device', title: 'This device', isWritable: true });
    }

    const link = await serverLink(bridge);
    if (link) {
        stores.push({ kind: 'casa', title: 'Tilez Hub', detail: describeServer(link), isWritable: true });
    }

    const cloud = await serverLink(bridge, 'cloud');
    if (cloud) {
        stores.push({ kind: 'cloud', title: 'Tilez Cloud', detail: describeServer(cloud), isWritable: true });
    }

    const settings = await readStoreSettings(bridge);
    if (settings.url) {
        stores.push({ kind: 'url', title: 'Web address', detail: settings.url.url, isWritable: false });
    }

    return stores;
}

/** The dashboards a store holds. A web address holds one: the one at that address. */
export async function listStore(bridge: Bridge, kind: StoreKind) : Promise<StoreEntry[]> {
    switch (kind) {
        case 'device':
            return (await listDeviceDashboards(bridge)).map((name) => ({ name }));
        case 'casa':
        case 'cloud': {
            const link = await serverLink(bridge, kind);

            return link ? (await listServerDashboards(bridge, link)).map(({ name, updated }) => ({ name, updated })) : [];
        }
        case 'url': {
            const { url } = await readStoreSettings(bridge);

            return url ? [{ name: url.url }] : [];
        }
    }
}

/** The text of a dashboard in a store, validated. */
export async function readFromStore(bridge: Bridge, kind: StoreKind, name: string) : Promise<string> {
    let text: string | undefined;
    switch (kind) {
        case 'device':
            text = await readDeviceDashboard(bridge, name);
            break;
        case 'casa':
        case 'cloud': {
            const link = await serverLink(bridge, kind);
            text = link ? (await readServerDashboard(bridge, link, name)).text : undefined;
            break;
        }
        case 'url': {
            const { url } = await readStoreSettings(bridge);
            text = url ? await fetchFromUrl(bridge, url) : undefined;
            break;
        }
    }
    if (text === undefined) {
        throw new Error(`"${name}" is not there any more.`);
    }

    parseOrThrow(text, `"${name}"`);

    return text;
}

/**
 * Changes dashboard `name` in a store, without changing what this device shows (a tile copied into
 * it, dashboard-editor L-14): `change` gets its text and gives the new text. On the server it is
 * written over the version read; if another device saved it in between, it is read and changed again,
 * once. Throws when the dashboard is not there, `change` throws, or it changed again.
 */
export async function changeInStore(bridge: Bridge, kind: WritableStore, name: string, change: (text: string) => string) : Promise<void> {
    if (kind === 'device') {
        const text = await readDeviceDashboard(bridge, name);
        if (text === undefined) {
            throw new Error(`"${name}" is not there any more.`);
        }

        await writeDeviceDashboard(bridge, name, change(text));

        return;
    }

    const link = await serverLink(bridge, kind);
    if (!link) {
        throw new Error(`This device is not paired with ${SERVER_NAMES[kind]}.`);
    }

    for (let attempt = 0; attempt < 2; attempt++) {
        const { text, version } = await readServerDashboard(bridge, link, name);
        if ((await writeServerDashboard(bridge, link, name, change(text), { version })).ok) {
            return;
        }
    }

    throw new Error(`"${name}" was changed on ${SERVER_NAMES[kind]} while the copy was made; try again.`);
}

/**
 * Copies a dashboard into another store under `to` (DB-7), without changing what this device shows.
 * False when that store has a dashboard by that name already.
 */
export async function copyToStore(bridge: Bridge, from: { kind: StoreKind; name: string }, to: { kind: WritableStore; name: string }) : Promise<boolean> {
    const text = await readFromStore(bridge, from.kind, from.name);
    if (to.kind === 'device') {
        if ((await findDeviceDashboard(bridge, to.name)) !== undefined) {
            return false;
        }

        await writeDeviceDashboard(bridge, to.name, text);

        return true;
    }

    const link = await serverLink(bridge, to.kind);
    if (!link) {
        throw new Error(`This device is not paired with ${SERVER_NAMES[to.kind]}.`);
    }

    return (await writeServerDashboard(bridge, link, to.name, text, 'new')).ok;
}
